import React, { Suspense } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import { TopBar } from './TopBar'
import { MobileNav } from './MobileNav'
import { useRole } from '@/hooks/useRole'
import { Footer } from './Footer'
import { ConsentBanner } from './ConsentBanner'
import { LaunchAnnouncement } from './LaunchAnnouncement'
import { ToastViewport } from '@/design-system/Toast'
import { Spinner } from '@/design-system/Spinner'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import { useOnlineStatus } from '@/hooks/useOnlineStatus'
import { mediaQueries } from '@/design-system/tokens'
import { SkipToContent } from './SkipToContent'
import { ScrollToTop } from './ScrollToTop'
import { WifiOff } from 'lucide-react'

/* ------------------------------------------------------------------ */
/*  AppShell — main layout composition                                 */
/*  TopBar (≥md) or compact top (md-), main Outlet, Footer,           */
/*  MobileNav, ToastViewport, ConsentBanner                            */
/*  See ARCHITECTURE.md §17                                            */
/* ------------------------------------------------------------------ */

function PageFallback() {
  return (
    <div className="flex min-h-[50vh] items-center justify-center">
      <Spinner size={24} />
    </div>
  )
}

export function AppShell() {
  const location = useLocation()
  const isDesktop = useMediaQuery(mediaQueries.md)
  const isOnline = useOnlineStatus()
  const { role } = useRole()

  return (
    <div className="flex min-h-screen flex-col overflow-x-hidden">
      {/* Scroll to top on route change */}
      <ScrollToTop />

      {/* Skip-to-content — WCAG 2.4.1 */}
      <SkipToContent />

      {/* Offline banner */}
      {!isOnline && (
        <div
          role="alert"
          className="flex items-center justify-center gap-2 bg-warning-500 px-4 py-2 text-center text-sm font-medium text-neutral-900"
        >
          <WifiOff className="h-4 w-4 shrink-0" aria-hidden="true" />
          Du är offline. Vissa funktioner kanske inte fungerar.
        </div>
      )}

      {/* Header */}
      <TopBar currentPath={location.pathname} />

      {/* Main content */}
      <main
        id="main-content"
        className="flex flex-1 flex-col pb-16 md:pb-0"
      >
        <Suspense fallback={<PageFallback />}>
          <Outlet />
        </Suspense>
      </main>

      {/* Footer — hidden on mobile when MobileNav shown */}
      {isDesktop && <Footer />}

      {/* Mobile bottom nav */}
      {!isDesktop && <MobileNav currentPath={location.pathname} role={role ?? 'guest'} />}

      {/* Toast viewport */}
      <ToastViewport />

      {/* Cookie consent */}
      <ConsentBanner />

      {/* Pre-launch preview popup (once per session) */}
      <LaunchAnnouncement />
    </div>
  )
}

AppShell.displayName = 'AppShell'
